import React, { useState, useEffect } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { NAV_ITEMS, scrollToSection } from '@/lib/index';
import { clsx, type ClassValue } from 'clsx';
import { twMerge } from 'tailwind-merge';

/**
 * Utility for tailwind class merging
 */
function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs));
}
interface LayoutProps {
  children: React.ReactNode;
}

/**
 * Layout component providing the core structure of the single-page portfolio.
 * Includes a sticky navigation header with smooth scroll capabilities and a clean footer.
 */
export function Layout({
  children
}: LayoutProps) {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);

  // Handle scroll events to update header appearance
  useEffect(() => {
    const handleScroll = () => {
      setIsScrolled(window.scrollY > 20);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Handle navigation clicks with smooth scroll
  const handleNavClick = (e: React.MouseEvent<HTMLAnchorElement>, href: string) => {
    e.preventDefault();
    scrollToSection(href);
    setIsMobileMenuOpen(false);
  };
  return <div className="min-h-screen flex flex-col bg-background text-foreground selection:bg-primary/20 selection:text-primary">
      {/* Navigation Header */}
      <header className={cn("fixed top-0 left-0 right-0 z-50 transition-all duration-300 h-20 flex items-center", isScrolled ? "bg-background/80 backdrop-blur-md border-b border-border/40 shadow-sm" : "bg-transparent")}>
        <nav className="container mx-auto px-6 flex items-center justify-between">
          {/* Logo / Brand */}
          <a href="#hero" onClick={e => handleNavClick(e, '#hero')} className="text-2xl font-bold tracking-tighter text-primary hover:opacity-80 transition-opacity font-sans">Youcef Benai</a>

          {/* Desktop Navigation Menu */}
          <ul className="hidden md:flex items-center gap-10">
            {NAV_ITEMS.map(item => <li key={item.href}>
                <a href={item.href} onClick={e => handleNavClick(e, item.href)} className="text-sm font-medium tracking-wide text-muted-foreground hover:text-primary transition-colors duration-200 uppercase">
                  {item.label}
                </a>
              </li>)}
          </ul>

          {/* Mobile Menu Toggle */}
          <button className="md:hidden p-2 text-foreground hover:bg-accent rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary/20" onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)} aria-label="Toggle menu">
            {isMobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </nav>
      </header>

      {/* Mobile Navigation Overlay */}
      <AnimatePresence>
        {isMobileMenuOpen && <motion.div initial={{
        opacity: 0,
        x: '100%'
      }} animate={{
        opacity: 1,
        x: 0
      }} exit={{
        opacity: 0,
        x: '100%'
      }} transition={{
        type: 'spring',
        damping: 25,
        stiffness: 200
      }} className="fixed inset-0 z-40 bg-background flex flex-col pt-32 px-10 md:hidden">
            <ul className="flex flex-col gap-8">
              {NAV_ITEMS.map(item => <li key={item.href}>
                  <a href={item.href} onClick={e => handleNavClick(e, item.href)} className="text-4xl font-bold tracking-tight text-foreground hover:text-primary transition-colors">
                    {item.label}
                  </a>
                </li>)}
            </ul>
            
            <div className="mt-auto mb-12">
              <p className="text-muted-foreground text-sm uppercase tracking-widest">
                Connect with me
              </p>
              <div className="mt-4 h-px w-12 bg-primary" />
            </div>
          </motion.div>}
      </AnimatePresence>

      {/* Main Content Area */}
      <main className="flex-grow">
        {children}
      </main>

      {/* Simple Professional Footer */}
      <footer className="py-16 border-t border-border/40 bg-muted/20">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-8">
            <div className="flex flex-col items-center md:items-start gap-4">
              <span className="text-2xl font-bold tracking-tighter text-primary">
                PORTFOLIO<span className="text-foreground">.</span>
              </span>
              <p className="text-sm text-muted-foreground max-w-xs text-center md:text-left leading-relaxed">
                A showcase of professional journey and creative endeavors, crafted with modern standards in 2026.
              </p>
            </div>
            
            <div className="flex flex-col items-center md:items-end gap-4">
              <nav className="flex gap-6">
                {NAV_ITEMS.map(item => <a key={item.href} href={item.href} onClick={e => handleNavClick(e, item.href)} className="text-xs font-semibold uppercase tracking-widest text-muted-foreground hover:text-primary transition-colors">
                    {item.label}
                  </a>)}
              </nav>
              <p className="text-xs font-mono text-muted-foreground/60">
                &copy; 2026 Professional Portfolio. All rights reserved.
              </p>
            </div>
          </div>
        </div>
      </footer>
    </div>;
}